import { BaseTool } from '../../core/BaseTool';
import { UploadManager } from './uploadManager';
import { ConversionManager } from './conversionManager';
import { UIController } from './uiController';
import { FormatSelector } from '../../ui/formatSelector';
import { AppIcon } from '../image-generator/appIcon';
import { TaglineManager } from '../image-generator/taglineManager';
import { Modal } from '../../ui/modal';
import { downloadBlob } from '../../utils/downloadHelper';

/**
 * ImageConverterTool - Convert images between different formats
 * Supports PNG, JPG, WebP, GIF, BMP, and TIFF formats
 */
export class ImageConverterTool extends BaseTool {
  private uploadManager: UploadManager;
  private conversionManager: ConversionManager;
  private uiController: UIController;
  private formatSelector: FormatSelector;
  private appIcon: AppIcon;
  private modal: Modal;
  private currentImage: HTMLImageElement | null = null;
  private currentFile: File | null = null;
  private selectedFormat: string = 'png';

  constructor() {
    super();
    this.uploadManager = new UploadManager();
    this.conversionManager = new ConversionManager();
    this.uiController = new UIController();
    this.formatSelector = new FormatSelector();
    this.modal = new Modal();
    this.appIcon = new AppIcon(this.modal);
  }

  async init(): Promise<void> {
    console.log('Initializing Image Converter Tool...');
    
    // Initialize app icon (for dynamic header logo)
    this.appIcon.setRandomIcon();

    // Initialize tagline (appends random tagline to description)
    new TaglineManager();

    // Initialize format selector with all supported formats
    this.formatSelector.init(
      'formatSelectorContainer',
      ['png', 'jpeg', 'webp', 'gif', 'bmp', 'tiff'],
      'png',
      this.handleFormatChange.bind(this)
    );
    
    // Initialize managers
    this.uiController.init();
    this.uploadManager.init(this.handleFileUpload.bind(this));
    
    // Setup event listeners
    this.setupEventListeners();
    
    this.setInitialized(true);
    console.log('Image Converter Tool initialized');
  }

  private setupEventListeners(): void {
    // Convert & Download button
    const generateBtn = document.getElementById('generateBtn');
    generateBtn?.addEventListener('click', () => {
      this.convertAndDownload();
    });

    // Clear image button
    const clearBtn = document.getElementById('clearImageBtn');
    clearBtn?.addEventListener('click', () => {
      this.clearImage();
    });
  }

  private clearImage(): void {
    // Clear current file and image
    this.currentFile = null;
    this.currentImage = null;

    // Hide uploaded image preview, show upload placeholder
    const uploadPlaceholder = document.getElementById('uploadPlaceholder');
    const uploadedPreview = document.getElementById('uploadedImagePreview');
    const clearBtn = document.getElementById('clearImageBtn');
    
    if (uploadPlaceholder) uploadPlaceholder.style.display = 'flex';
    if (uploadedPreview) uploadedPreview.style.display = 'none';
    if (clearBtn) clearBtn.style.display = 'none';

    // Hide file info
    this.uiController.hideFileInfo();

    // Disable generate button
    const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
    if (generateBtn) {
      generateBtn.disabled = true;
    }

    // Reset file input
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private handleFormatChange(format: string): void {
    this.selectedFormat = format;
    
    // Enable the button if we have a file
    if (this.currentFile) {
      const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
      if (generateBtn) {
        generateBtn.disabled = false;
      }
    }
  }

  private async handleFileUpload(file: File): Promise<void> {
    try {
      this.currentFile = file;
      
      // Load the image
      const img = await this.loadImage(file);
      this.currentImage = img;

      // Show the image in the upload zone
      const previewImage = document.getElementById('previewImage') as HTMLImageElement;
      const uploadPlaceholder = document.getElementById('uploadPlaceholder');
      const uploadedPreview = document.getElementById('uploadedImagePreview');
      const clearBtn = document.getElementById('clearImageBtn');
      
      if (previewImage && img.src) {
        previewImage.src = img.src;
        
        // Hide placeholder, show uploaded image and clear button
        if (uploadPlaceholder) uploadPlaceholder.style.display = 'none';
        if (uploadedPreview) uploadedPreview.style.display = 'block';
        if (clearBtn) clearBtn.style.display = 'flex';
      }

      // Show selected file info
      this.uiController.showFileInfo({
        name: file.name,
        format: file.type.split('/')[1].toUpperCase(),
        width: img.naturalWidth,
        height: img.naturalHeight,
        size: file.size
      });

      // Enable the generate button
      const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
      if (generateBtn) {
        generateBtn.disabled = false;
      }

    } catch (error) {
      console.error('Error loading image:', error);
      alert('Error loading image. Please try again.');
    }
  }

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          resolve(img);
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  private async convertAndDownload(): Promise<void> {
    if (!this.currentImage || !this.selectedFormat || !this.currentFile) {
      return;
    }

    try {
      // Show converting state
      const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
      const btnText = generateBtn?.querySelector('span');
      const originalText = btnText?.textContent;
      
      if (generateBtn) {
        generateBtn.disabled = true;
      }
      if (btnText) {
        btnText.textContent = 'Converting...';
      }

      // Convert the image
      const convertedBlob = await this.conversionManager.convert(
        this.currentImage,
        this.selectedFormat
      );

      // Download the converted image immediately
      this.downloadImage(convertedBlob);

      // Reset button
      if (generateBtn) {
        generateBtn.disabled = false;
      }
      if (btnText && originalText) {
        btnText.textContent = originalText;
      }

    } catch (error) {
      console.error('Error converting image:', error);
      alert('Error converting image. Please try again.');
      
      // Reset button
      const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
      const btnText = generateBtn?.querySelector('span');
      if (generateBtn) {
        generateBtn.disabled = false;
      }
      if (btnText) {
        btnText.textContent = 'Convert & Download';
      }
    }
  }

  private downloadImage(blob: Blob): void {
    if (!this.currentFile) {
      return;
    }

    // Generate filename
    const originalName = this.currentFile.name;
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
    const newFilename = `${nameWithoutExt}-converted.${this.selectedFormat === 'jpeg' ? 'jpg' : this.selectedFormat}`;
    
    // Use cross-browser compatible download helper
    downloadBlob(blob, newFilename);
  }

  destroy(): void {
    this.uploadManager.destroy();
    this.currentImage = null;
    this.currentFile = null;
    this.setInitialized(false);
    console.log('Image Converter Tool destroyed');
  }

  getToolName(): string {
    return 'Image Converter';
  }
}

// Initialize the tool
const tool = new ImageConverterTool();
tool.init();
